import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Crown, Users, ChevronDown, ChevronUp, TrendingUp, TrendingDown, Minus, Zap, Flame, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface LeaderboardEntry {
  rank: number;
  gamified_name: string;
  username?: string;
  displayName?: string;
  verified_count: number;
  velocity?: string;
  last_referral?: string;
  trending: 'up' | 'down' | 'stable';
}

interface PaginationMeta {
  current_page: number;
  total_pages: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

interface LiveLeaderboardProps {
  challengeId: string;
  entries: LeaderboardEntry[];
  totalParticipants: number;
  userRank?: number;
  userVerifiedCount?: number;
  userGamifiedName?: string;
  isLive?: boolean;
  pagination?: PaginationMeta;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  loading?: boolean;
}

interface AnimatedEntry extends LeaderboardEntry {
  previousRank?: number;
  previousCount?: number;
  isMovingUp?: boolean;
  isMovingDown?: boolean;
  isNew?: boolean;
  countIncreased?: boolean;
}

const getRankBadge = (rank: number) => {
  if (rank === 1) return { icon: '👑', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', bgGradient: 'from-yellow-50 to-orange-50' };
  if (rank === 2) return { icon: '🥈', color: 'bg-gray-100 text-gray-800 border-gray-300', bgGradient: 'from-gray-50 to-gray-100' };
  if (rank === 3) return { icon: '🥉', color: 'bg-orange-100 text-orange-800 border-orange-300', bgGradient: 'from-orange-50 to-red-50' };
  return { icon: `#${rank}`, color: 'bg-blue-100 text-blue-800 border-blue-300', bgGradient: 'from-blue-50 to-purple-50' };
};

// Animated number component
const AnimatedNumber = ({ value, previousValue }: { value: number; previousValue?: number }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (previousValue !== undefined && previousValue !== value) {
      setIsAnimating(true);
      
      // Animate the number change
      const duration = 800;
      const steps = 20;
      const increment = (value - previousValue) / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const newValue = previousValue + (increment * currentStep);
        setDisplayValue(Math.round(newValue));

        if (currentStep >= steps) {
          clearInterval(timer);
          setDisplayValue(value);
          setIsAnimating(false);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else {
      setDisplayValue(value);
    }
  }, [value, previousValue]);

  return (
    <span className={`transition-all duration-300 ${isAnimating ? 'text-green-600 scale-110' : ''}`}>
      {displayValue}
    </span>
  );
};

// Position change indicator
const PositionChangeIndicator = ({ currentRank, previousRank }: { currentRank: number; previousRank?: number }) => {
  if (!previousRank || previousRank === currentRank) return null;

  const isMovingUp = currentRank < previousRank;
  const positions = Math.abs(currentRank - previousRank);

  return (
    <div className={`flex items-center gap-1 animate-bounce ${isMovingUp ? 'text-green-500' : 'text-red-500'}`}>
      {isMovingUp ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      <span className="text-xs font-bold">+{positions}</span>
    </div>
  );
};

export function EnhancedLiveLeaderboard({ 
  challengeId, 
  entries, 
  totalParticipants, 
  userRank, 
  userVerifiedCount,
  userGamifiedName,
  isLive = true,
  pagination,
  onPageChange,
  onPageSizeChange,
  loading = false
}: LiveLeaderboardProps) {
  const [animatedEntries, setAnimatedEntries] = useState<AnimatedEntry[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date>(new Date());
  const previousEntriesRef = useRef<LeaderboardEntry[]>([]);
  const { toast } = useToast();

  // Use all entries as display entries since pagination is handled by API
  const displayEntries = animatedEntries;

  // Enhanced change detection with animations
  useEffect(() => {
    const previousEntries = previousEntriesRef.current;
    
    if (previousEntries.length === 0) {
      // First load - no animations
      setAnimatedEntries(entries.map(entry => ({ ...entry })));
      previousEntriesRef.current = [...entries];
      return;
    }

    // Create a map of previous entries for quick lookup
    const previousMap = new Map(previousEntries.map(entry => [entry.gamified_name, entry]));

    // Process each current entry to detect changes
    const newAnimatedEntries: AnimatedEntry[] = entries.map(entry => {
      const previous = previousMap.get(entry.gamified_name);
      
      if (!previous) {
        // New participant - show toast notification
        toast({
          title: "🎉 New participant!",
          description: `${entry.gamified_name} joined the challenge!`,
          duration: 5000,
        });
        return {
          ...entry,
          isNew: true,
        };
      }

      const animatedEntry: AnimatedEntry = {
        ...entry,
        previousRank: previous.rank,
        previousCount: previous.verified_count,
      };

      // Detect rank changes
      if (previous.rank !== entry.rank) {
        if (entry.rank < previous.rank) {
          animatedEntry.isMovingUp = true;
          const positions = previous.rank - entry.rank;
          toast({
            title: "📈 Position change!",
            description: `${entry.gamified_name} moved up ${positions} position${positions > 1 ? 's' : ''} to #${entry.rank}!`,
            duration: 5000,
          });
        } else {
          animatedEntry.isMovingDown = true;
        }
      }

      // Detect referral count increases
      if (previous.verified_count < entry.verified_count) {
        animatedEntry.countIncreased = true;
        const increase = entry.verified_count - previous.verified_count;
        toast({
          title: "🚀 New referrals!",
          description: `${entry.gamified_name} got ${increase} new referral${increase > 1 ? 's' : ''}!`,
          duration: 5000,
        });
      }

      return animatedEntry;
    });

    setAnimatedEntries(newAnimatedEntries);
    setLastUpdateTime(new Date());
    previousEntriesRef.current = [...entries];

    // Clear change indicators after animation
    setTimeout(() => {
      setAnimatedEntries(prev => prev.map(entry => ({
        ...entry,
        isMovingUp: false,
        isMovingDown: false,
        isNew: false,
        countIncreased: false,
      })));
    }, 2000);

  }, [entries]);

  return (
    <div className="space-y-4">
      <Card className="w-full bg-white border-2 border-gray-200 shadow-sm overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-500 text-white border-b border-gray-200 pb-4">
          {/* Header matching the design */}
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Crown className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-xl font-bold text-white">Live Leaderboard</CardTitle>
              <div className="flex items-center gap-4 mt-1">
                <div className="flex items-center gap-1 text-sm text-white/90">
                  <Users className="h-4 w-4" />
                  <AnimatedNumber value={totalParticipants} />
                  <span>participants</span>
                </div>
                {isLive && (
                  <Badge className="bg-red-500 text-white border-red-400 text-sm px-2 py-1 animate-pulse">
                    🔴 LIVE
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          {userRank && (
            <div className="mt-3">
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 px-3 py-1 text-sm">
                You: #{userRank}
              </Badge>
            </div>
          )}
        </CardHeader>

        <CardContent className="p-0">
          {/* Enhanced Leaderboard with Animations */}
          <div className="divide-y divide-gray-100">
            {displayEntries.map((entry, index) => {
              const rankBadge = getRankBadge(entry.rank);
              const isTopThree = entry.rank <= 3;
              
              
              // Accurate user matching using gamified name (most reliable method)
              const isUser = userGamifiedName && userGamifiedName === entry.gamified_name;
              
              return (
                <div
                  key={`${entry.rank}-${entry.gamified_name}`}
                  className={`
                    px-4 py-3 transition-all duration-500 relative overflow-visible
                    ${isUser ? 'bg-blue-100 border-2 border-blue-500 shadow-md ring-2 ring-blue-200' : 'hover:bg-gray-50'}
                    ${isTopThree && !isUser ? `bg-gradient-to-r ${rankBadge.bgGradient}` : ''}
                    ${entry.isNew ? 'animate-in slide-in-from-right-2 fade-in duration-1000' : ''}
                    ${entry.isMovingUp && !isUser ? 'bg-green-50 border-l-4 border-l-green-500' : ''}
                    ${entry.isMovingDown && !isUser ? 'bg-red-50 border-l-4 border-l-red-500' : ''}
                  `}
                >
                  {/* Celebration effect for new entries */}
                  {entry.isNew && (
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-200/20 to-orange-200/20 animate-pulse" />
                  )}

                  {/* Layout matching the design */}
                  <div className="flex items-center gap-3 relative z-10">
                    {/* Left side - Rank Badge */}
                    <div className="relative">
                      <Badge className={`${rankBadge.color} font-bold text-sm px-3 py-1 shrink-0 transition-all duration-300`}>
                        {rankBadge.icon}
                      </Badge>
                      <PositionChangeIndicator 
                        currentRank={entry.rank} 
                        previousRank={entry.previousRank} 
                      />
                    </div>
                    
                    {/* Middle - Avatar and Username */}
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`
                        px-3 py-1 rounded-lg flex items-center text-white font-bold text-sm shrink-0 transition-all duration-300
                        ${isTopThree ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 'bg-gradient-to-r from-blue-500 to-purple-500'}
                        ${entry.isNew ? 'animate-bounce' : ''}
                      `}>
                        {entry.gamified_name || entry.displayName || entry.username || 'Unknown User'}
                      </div>
                      
                      <div className="flex-1 overflow-visible">
                        {entry.isNew && <span className="ml-2 text-green-600 text-xs font-semibold">NEW!</span>}
                        {isUser && (
                          <div className="text-xs text-blue-600 font-medium">You</div>
                        )}
                      </div>
                    </div>

                    {/* Right side - Referrals count */}
                    <div className="text-right shrink-0">
                      <div className={`
                        text-2xl font-bold transition-all duration-500
                        ${entry.countIncreased ? 'text-green-600 scale-110' : 'text-gray-900'}
                      `}>
                        <AnimatedNumber 
                          value={entry.verified_count} 
                          previousValue={entry.previousCount} 
                        />
                      </div>
                      <div className="text-sm text-blue-600 font-medium">
                        referrals
                        {/* Movement indicator */}
                        {entry.trending === 'up' && (
                          <span className="ml-1 text-blue-500">↗ 0/hour/h</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination && onPageChange && onPageSizeChange && (
            <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50">
              {/* Rows per page selector */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rows per page:</span>
                    <Select 
                      value={pagination.page_size.toString()} 
                      onValueChange={(value) => onPageSizeChange(parseInt(value))}
                      data-testid="select-page-size"
                    >
                      <SelectTrigger className="w-16 sm:w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                
                <div className="text-sm text-gray-600">
                  Showing {Math.min((pagination.current_page - 1) * pagination.page_size + 1, totalParticipants)} - {Math.min(pagination.current_page * pagination.page_size, totalParticipants)} of {totalParticipants} participants
                </div>
              </div>

              {/* Pagination buttons */}
              <div className="flex items-center justify-center gap-2">
                <Button
                  onClick={() => onPageChange(pagination.current_page - 1)}
                  disabled={!pagination.has_prev || loading}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {/* Show page numbers around current page */}
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        onClick={() => onPageChange(pageNum)}
                        variant={pageNum === pagination.current_page ? "default" : "outline"}
                        size="sm"
                        className="w-8 h-8 p-0"
                        data-testid={`button-page-${pageNum}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  onClick={() => onPageChange(pagination.current_page + 1)}
                  disabled={!pagination.has_next || loading}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="text-center text-xs text-gray-500 mt-2">
                Page {pagination.current_page} of {pagination.total_pages}
              </div>
            </div>
          )}

          {/* Empty State */}
          {displayEntries.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <div className="text-lg font-semibold mb-2">No participants yet</div>
              <div className="text-sm">Be the first to join this challenge!</div>
            </div>
          )}

          {/* Enhanced Last Updated with pulse animation */}
          {isLive && displayEntries.length > 0 && (
            <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <span>Updated: {lastUpdateTime.toLocaleTimeString()}</span>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
